import formValidation from 'formvalidation/core/Core';
import SelectorEngine from '../../utils/dom/selector-engine';

import Request from '../../utils/request';

import { scrollToTop, scrollToNode } from '../../utils/misc';

import ValidationUtils from '../../utils/validation';
import Modal from '../../components/Modal';
import passwordValidationRules from './password-validation-rules';

class EditPassword {
	constructor() {
		this._container = document.querySelector('.js-edit-password');
		this._cancelCta = undefined;
		this._backCta = undefined;
		this._exitModal = undefined;

		if (this._container) {
			this.initiateValidation();
		}
	}

	initiateValidation() {
		this._exitModal = new Modal(SelectorEngine.findOne('#exit-editing'), {});
		this.editPasswordForm = SelectorEngine.findOne('#editPasswordForm');

		this.errorWrapper = SelectorEngine.findOne('.js-password--error', this._container);
		this.errorDefault = SelectorEngine.findOne('.js-password--error-default', this._container);
		this.errorFieldsContainer = SelectorEngine.findOne('.js-password__fields-error', this._container);

		this.errorDismissCta = SelectorEngine.findOne('.js-dismiss-password-error', this._container);
		this.errorDismissCta.addEventListener('click', () => {
			this.errorWrapper.classList.add('u-hidden');
		});

		this.errorDefaultDismissCta = SelectorEngine.findOne('.js-dismiss-password-error-default', this._container);
		this.errorDefaultDismissCta.addEventListener('click', () => {
			this.errorDefault.classList.add('u-hidden');
		});

		this.errorFieldsWrapper = SelectorEngine.findOne('.js-password__fields-error-content', this.errorFieldsContainer);
		this.fieldsErrorsList = SelectorEngine.findOne('ul', this.errorFieldsWrapper);
		this.fieldsErrorsList.textContent = '';

		// exit modal confirmation
		this._cancelCta = SelectorEngine.findOne('.js-account-exit', this._wrapper);
		this._cancelCta.addEventListener('click', (event) => this.backToUrl(event));

		this._backCta = SelectorEngine.findOne('.js-account-edit-back', this._wrapper);
		this._backCta.addEventListener('click', (event) => this.exitEditing(event));

		const popupCancel = SelectorEngine.findOne('.js-account-details-continue');
		popupCancel.addEventListener('click', () => this._exitModal.hide());

		const popupContinue = SelectorEngine.findOne('.js-account-details-exit');
		popupContinue.addEventListener('click', (event) => this.backToUrl(event));

		if (this.editPasswordForm !== null) {
			const fv = formValidation(this.editPasswordForm, passwordValidationRules(this.editPasswordForm))
				.on('core.field.invalid', (name) => this.fieldInvalid(name))
				.on('core.field.valid', (name) => this.fieldValid(name))
				.on('core.validator.validated', (event) => this.validatorValidated(event))
				.on('core.form.invalid', () => this.formInvalid())
				.on('core.form.validating', () => this.formValidating())
				.on('plugins.message.displayed', (e) => this.messageDisplayed(e))
				.on('core.form.valid', () => this.formValid());
		}
	}

	fieldInvalid(name) {
		const invalidField = document.getElementsByName(name)[0];
		const invalidFieldType = invalidField.getAttribute('type');
		const fieldWrapper = invalidField.closest(ValidationUtils.getFieldWrapper(invalidFieldType));
		fieldWrapper.classList.add(...ValidationUtils.getErrorClasses(invalidFieldType));
	}

	fieldValid(name) {
		const validField = document.getElementsByName(name)[0];
		const validFieldType = validField.getAttribute('type');
		const fieldWrapper = validField.closest(ValidationUtils.getFieldWrapper(validFieldType));
		fieldWrapper.classList.remove(...ValidationUtils.getErrorClasses(validFieldType));
	}

	validatorValidated(event) {
		if (event.result.valid) {
			// remove error messages that are now valid
			const errorMessages = SelectorEngine.find(
				`.js-validation-${event.field}--${event.validator}`,
				this.fieldsErrorsList
			);
			if (errorMessages) {
				[...errorMessages].forEach((element) => {
					element.remove();
				});
			}

			// if there are no more error messages, remove error messages container
			if (this.fieldsErrorsList.children.length === 0) {
				this.errorFieldsContainer.classList.add('u-hidden');
			}
		}
	}

	formInvalid() {
		this.errorFieldsContainer.classList.remove('u-hidden');
		scrollToTop();

		// password input is cleared if it does not meet password policies
		const passwordInputs = SelectorEngine.find('input[type=password]', this.editPasswordForm);
		[...passwordInputs].forEach((input) => {
			input.value = '';
		});
	}

	formValidating() {
		this.fieldsErrorsList.textContent = '';
	}

	messageDisplayed(e) {
		// checks if error message isn't already added
		if (!SelectorEngine.findOne(`.js-validation-${e.field}--${e.validator}`, this.fieldsErrorsList)) {
			const listItem = document.createElement('li');
			const listSpan = document.createElement('span');
			const listLink = document.createElement('a');
			listLink.setAttribute('href', '#');
			listItem.setAttribute('class', `js-validation-${e.field} js-validation-${e.field}--${e.validator}`);
			listLink.append(document.createTextNode(e.message));
			listSpan.append(listLink);
			listItem.append(listSpan);

			this.fieldsErrorsList.append(listItem);

			listLink.addEventListener('click', (event) => {
				event.preventDefault();
				scrollToNode(e.element);
				e.element.focus();
			});
		}
	}

	async formValid() {
		this.errorFieldsContainer.classList.add('u-hidden');
		const formData = new FormData(this.editPasswordForm);
		const formAction = this.editPasswordForm.querySelector('.js-edit-password-btn');
		formAction.disabled = true;
		this._cancelCta.disabled = true;

		const dataObject = {};
		formData.forEach((value, key) => {
			dataObject[key] = value;
		});

		const finalData = Object.assign({}, dataObject, { returnUrl: window.location.href });

		const apiResponse = await Request.post(this.editPasswordForm.dataset.url, finalData).catch(() => {
			this.errorWrapper.classList.remove('u-hidden');
			scrollToTop();
		});

		formAction.disabled = false;
		this._cancelCta.disabled = false;
		if (apiResponse.statusCode !== '02') {
			const { response } = apiResponse;

			const responseParts = response.split(/\n/);
			const errorTitle = responseParts.shift();
			const errorMessage = this.errorDefault.querySelector('.title');
			errorMessage.innerHTML = errorTitle;

			const errorList = this.errorDefault.querySelector('ul');
			errorList.textContent = '';
			if (responseParts.length > 0) {
				responseParts.forEach((part) => {
					const liElement = document.createElement('li');
					const spanElement = document.createElement('span');
					spanElement.textContent = part;
					liElement.append(spanElement);
					errorList.append(liElement);
				});
			}
			this.errorDefault.classList.remove('u-hidden');
			scrollToTop();
		} else {
			this.backToUrl(null, formAction.dataset.target);
		}
	}

	exitEditing() {
		this._exitModal.show();
	}

	backToUrl(event, redirectUrl = null) {
		const rootUrl = window.location.origin;
		const url = redirectUrl || event.currentTarget.dataset.target;
		window.location.href = `${rootUrl}${url}`;
	}
}

const globalEditPassword = new EditPassword();

export default globalEditPassword;
